import * as IF from "infrontjs";
import { InAppState } from "../base/InAppState";
import template from './PackCreateTemplate.html';

import { Template } from "../../models/Template";

export class PackCreateState extends InAppState
{
    static ROUTE = '/pack/create';
    static ID = 'pack-create';

    async enter()
    {
        this.availableTemplates = [];
        this.selectedTemplateIds = [];

        const { result } = await this.app.api.get( '/template' );
        if ( result.status === 200 && result.data.length > 0 )
        {
            result.data.forEach( ( t ) =>
            {
                this.availableTemplates.push( new Template( t ) );
            });
        }

        this.render();
        this._initDragAndDrop();
        this._bindSearch();
        this._bindSubmit();
    }

    // ─── Drag & Drop ────────────────────────────────────────

    _initDragAndDrop()
    {
        const pageContent = this.app.view.pageContent;
        const availableList = pageContent.querySelector( '#available-templates' );
        const selectedList = pageContent.querySelector( '#selected-templates' );

        this._draggedEl = null;
        this._dragPlaceholder = this._createPlaceholder();

        // Bind add-buttons (arrow click to move)
        this._bindAddButtons();

        // Available list: drag start
        this.addEventListener( availableList, 'dragstart', ( e ) =>
        {
            const item = e.target.closest( '.template-dnd-item' );
            if ( !item ) return;
            this._draggedEl = item;
            item.classList.add( 'dnd-dragging' );
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData( 'text/plain', item.dataset.templateId );
        });

        this.addEventListener( availableList, 'dragend', ( e ) =>
        {
            this._cleanupDrag();
        });

        // Selected list: drag start (for reordering)
        this.addEventListener( selectedList, 'dragstart', ( e ) =>
        {
            const item = e.target.closest( '.template-dnd-item' );
            if ( !item ) return;
            this._draggedEl = item;
            item.classList.add( 'dnd-dragging' );
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData( 'text/plain', item.dataset.templateId );

            // Collapse the dragged element after the browser captures the drag image,
            // so other items shift and _getDragAfterElement calculates correct positions
            requestAnimationFrame( () =>
            {
                item.style.display = 'none';
            });
        });

        this.addEventListener( selectedList, 'dragend', ( e ) =>
        {
            this._cleanupDrag();
        });

        // Selected list: accept drops
        this.addEventListener( selectedList, 'dragover', ( e ) =>
        {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            selectedList.classList.add( 'dnd-over' );

            const afterElement = this._getDragAfterElement( selectedList, e.clientY );
            if ( afterElement )
            {
                selectedList.insertBefore( this._dragPlaceholder, afterElement );
            }
            else
            {
                selectedList.appendChild( this._dragPlaceholder );
            }
            this._dragPlaceholder.style.display = 'block';
        });

        this.addEventListener( selectedList, 'dragleave', ( e ) =>
        {
            if ( !selectedList.contains( e.relatedTarget ) )
            {
                selectedList.classList.remove( 'dnd-over' );
                this._dragPlaceholder.style.display = 'none';
            }
        });

        this.addEventListener( selectedList, 'drop', ( e ) =>
        {
            e.preventDefault();
            selectedList.classList.remove( 'dnd-over' );
            this._dragPlaceholder.style.display = 'none';

            if ( !this._draggedEl ) return;

            const templateId = this._draggedEl.dataset.templateId;
            const isFromAvailable = this._draggedEl.closest( '#available-templates' ) !== null;

            if ( isFromAvailable )
            {
                this._moveToSelected( templateId );
            }
            else
            {
                // Reorder within selected – insert at the placeholder's position
                if ( this._dragPlaceholder.parentNode === selectedList )
                {
                    selectedList.insertBefore( this._draggedEl, this._dragPlaceholder );
                }
                else
                {
                    selectedList.appendChild( this._draggedEl );
                }
                this._syncSelectedOrderFromDom();
            }

            this._cleanupDrag();
        });

        // Allow dragging back to available list (remove from pack)
        this.addEventListener( availableList, 'dragover', ( e ) =>
        {
            if ( this._draggedEl && this._draggedEl.closest( '#selected-templates' ) )
            {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                availableList.classList.add( 'dnd-over' );
            }
        });

        this.addEventListener( availableList, 'dragleave', ( e ) =>
        {
            if ( !availableList.contains( e.relatedTarget ) )
            {
                availableList.classList.remove( 'dnd-over' );
            }
        });

        this.addEventListener( availableList, 'drop', ( e ) =>
        {
            e.preventDefault();
            availableList.classList.remove( 'dnd-over' );

            if ( !this._draggedEl ) return;

            const templateId = this._draggedEl.dataset.templateId;
            const isFromSelected = this._draggedEl.closest( '#selected-templates' ) !== null;

            if ( isFromSelected )
            {
                this._moveToAvailable( templateId );
            }

            this._cleanupDrag();
        });
    }

    _createPlaceholder()
    {
        const el = document.createElement( 'div' );
        el.className = 'template-dnd-placeholder';
        el.style.display = 'none';
        return el;
    }

    _cleanupDrag()
    {
        if ( this._draggedEl )
        {
            this._draggedEl.classList.remove( 'dnd-dragging' );
            this._draggedEl = null;
        }
        this._dragPlaceholder.style.display = 'none';
        if ( this._dragPlaceholder.parentNode )
        {
            this._dragPlaceholder.parentNode.removeChild( this._dragPlaceholder );
        }

        const pageContent = this.app.view.pageContent;
        pageContent.querySelector( '#available-templates' )?.classList.remove( 'dnd-over' );
        pageContent.querySelector( '#selected-templates' )?.classList.remove( 'dnd-over' );
    }

    _getDragAfterElement( container, y )
    {
        const items = [ ...container.querySelectorAll( '.template-dnd-item:not(.dnd-dragging)' ) ];
        let closest = null;
        let closestOffset = Number.NEGATIVE_INFINITY;

        items.forEach( ( child ) =>
        {
            const box = child.getBoundingClientRect();
            const offset = y - box.top - box.height / 2;
            if ( offset < 0 && offset > closestOffset )
            {
                closestOffset = offset;
                closest = child;
            }
        });

        return closest;
    }

    // ─── Move items between lists ───────────────────────────

    _moveToSelected( templateId )
    {
        if ( this.selectedTemplateIds.includes( templateId ) ) return;

        this.selectedTemplateIds.push( templateId );

        const pageContent = this.app.view.pageContent;
        const availableList = pageContent.querySelector( '#available-templates' );
        const selectedList = pageContent.querySelector( '#selected-templates' );

        const item = availableList.querySelector( `[data-template-id="${templateId}"]` );
        if ( !item ) return;

        // Remove from available
        item.remove();

        // Create selected item
        const selectedItem = this._createSelectedItem( templateId );

        // Insert at placeholder position or append
        const afterElement = this._getDragAfterElement( selectedList, Infinity );
        if ( afterElement )
        {
            selectedList.insertBefore( selectedItem, afterElement );
        }
        else
        {
            selectedList.appendChild( selectedItem );
        }

        this._updateCounts();
        this._updateDropHint();
        this._bindRemoveButtons();
        this._syncSelectedOrderFromDom();
    }

    _moveToAvailable( templateId )
    {
        const idx = this.selectedTemplateIds.indexOf( templateId );
        if ( idx === -1 ) return;

        this.selectedTemplateIds.splice( idx, 1 );

        const pageContent = this.app.view.pageContent;
        const availableList = pageContent.querySelector( '#available-templates' );
        const selectedList = pageContent.querySelector( '#selected-templates' );

        // Remove from selected list
        const item = selectedList.querySelector( `[data-template-id="${templateId}"]` );
        if ( item ) item.remove();

        // Remove "No templates available" placeholder if present
        const emptyHint = availableList.querySelector( '.template-dnd-empty' );
        if ( emptyHint ) emptyHint.remove();

        // Re-add to available list
        const availableItem = this._createAvailableItem( templateId );
        if ( availableItem )
        {
            availableList.appendChild( availableItem );
        }

        this._updateCounts();
        this._updateDropHint();
        this._bindAddButtons();
    }

    _createSelectedItem( templateId )
    {
        const t = this.availableTemplates.find( ( tmpl ) => tmpl.id === templateId );
        if ( !t ) return null;

        const div = document.createElement( 'div' );
        div.className = 'template-dnd-item template-dnd-item-selected';
        div.draggable = true;
        div.dataset.templateId = t.id;

        const statusBadge = t.status === 'ACTIVE'
            ? '<span class="badge bg-success-subtle text-success badge-sm">Active</span>'
            : '<span class="badge bg-warning-subtle text-warning badge-sm">Inactive</span>';

        div.innerHTML = `
            <div class="template-dnd-item-grip">
                <i class="ti ti-grip-vertical"></i>
            </div>
            <div class="template-dnd-item-order">
                <span class="template-dnd-order-number"></span>
            </div>
            <div class="template-dnd-item-content">
                <span class="template-dnd-item-name">${this._escapeHtml( t.name )}</span>
                ${statusBadge}
            </div>
            <button type="button" class="btn btn-sm btn-outline-danger template-dnd-remove" title="Remove from pack">
                <i class="ti ti-x"></i>
            </button>
        `;

        return div;
    }

    _createAvailableItem( templateId )
    {
        const t = this.availableTemplates.find( ( tmpl ) => tmpl.id === templateId );
        if ( !t ) return null;

        const div = document.createElement( 'div' );
        div.className = 'template-dnd-item';
        div.draggable = true;
        div.dataset.templateId = t.id;

        const statusBadge = t.status === 'ACTIVE'
            ? '<span class="badge bg-success-subtle text-success badge-sm">Active</span>'
            : '<span class="badge bg-warning-subtle text-warning badge-sm">Inactive</span>';

        div.innerHTML = `
            <div class="template-dnd-item-grip">
                <i class="ti ti-grip-vertical"></i>
            </div>
            <div class="template-dnd-item-content">
                <span class="template-dnd-item-name">${this._escapeHtml( t.name )}</span>
                ${statusBadge}
            </div>
            <button type="button" class="btn btn-sm btn-outline-primary template-dnd-add" title="Add to pack">
                <i class="ti ti-chevron-right"></i>
            </button>
        `;

        return div;
    }

    // ─── Button bindings ────────────────────────────────────

    _bindAddButtons()
    {
        const pageContent = this.app.view.pageContent;
        const addBtns = pageContent.querySelectorAll( '#available-templates .template-dnd-add' );
        addBtns.forEach( ( btn ) =>
        {
            btn.onclick = ( e ) =>
            {
                e.preventDefault();
                const item = btn.closest( '.template-dnd-item' );
                if ( item )
                {
                    this._moveToSelected( item.dataset.templateId );
                }
            };
        });
    }

    _bindRemoveButtons()
    {
        const pageContent = this.app.view.pageContent;
        const removeBtns = pageContent.querySelectorAll( '#selected-templates .template-dnd-remove' );
        removeBtns.forEach( ( btn ) =>
        {
            btn.onclick = ( e ) =>
            {
                e.preventDefault();
                const item = btn.closest( '.template-dnd-item' );
                if ( item )
                {
                    this._moveToAvailable( item.dataset.templateId );
                }
            };
        });
    }

    // ─── Search ─────────────────────────────────────────────

    _bindSearch()
    {
        const pageContent = this.app.view.pageContent;
        const searchInput = pageContent.querySelector( '#template-search' );
        if ( !searchInput ) return;

        this.addEventListener( searchInput, 'input', () =>
        {
            const query = searchInput.value.toLowerCase().trim();
            const items = pageContent.querySelectorAll( '#available-templates .template-dnd-item' );
            items.forEach( ( item ) =>
            {
                const name = item.querySelector( '.template-dnd-item-name' )?.textContent.toLowerCase() || '';
                item.style.display = name.includes( query ) ? '' : 'none';
            });
        });
    }

    // ─── Helpers ────────────────────────────────────────────

    _syncSelectedOrderFromDom()
    {
        const pageContent = this.app.view.pageContent;
        const selectedList = pageContent.querySelector( '#selected-templates' );
        const items = selectedList.querySelectorAll( '.template-dnd-item' );

        this.selectedTemplateIds = [];
        items.forEach( ( item, index ) =>
        {
            this.selectedTemplateIds.push( item.dataset.templateId );
            const orderNum = item.querySelector( '.template-dnd-order-number' );
            if ( orderNum )
            {
                orderNum.textContent = index + 1;
            }
        });

        this._updateCounts();
    }

    _updateCounts()
    {
        const pageContent = this.app.view.pageContent;
        const availableCount = pageContent.querySelector( '#available-count' );
        const selectedCount = pageContent.querySelector( '#selected-count' );
        const availableItems = pageContent.querySelectorAll( '#available-templates .template-dnd-item' );

        if ( availableCount ) availableCount.textContent = availableItems.length;
        if ( selectedCount ) selectedCount.textContent = this.selectedTemplateIds.length;
    }

    _updateDropHint()
    {
        const pageContent = this.app.view.pageContent;
        const hint = pageContent.querySelector( '#drop-hint' );
        if ( hint )
        {
            hint.style.display = this.selectedTemplateIds.length > 0 ? 'none' : '';
        }
    }

    // ─── Submit ─────────────────────────────────────────────

    _bindSubmit()
    {
        const pageContent = this.app.view.pageContent;
        const form = pageContent.querySelector( '#pack-form' );

        this.addEventListener( pageContent.querySelector( '[data-id="submit-form"]'), 'click', async ( e ) =>
        {
            e.preventDefault();

            if ( !form.checkValidity() )
            {
                form.classList.add( 'was-validated' );
                return;
            }

            form.classList.remove( 'was-validated' );
            this.app.view.blockUI( pageContent, 'Creating pack...' );

            const td = IF.Helper.serializeForm( form );

            try
            {
                const { result } = await this.app.api.post( '/pack', {
                    "name" : td.name,
                    "description" : IF.Helper.trim( td.description ).length ? td.description : null,
                    "templateIds" : this.selectedTemplateIds
                });

                this.app.view.unblockUI( pageContent );

                if ( result && result.status === 201 )
                {
                    this.app.view.toast( 'Pack created successfully', 'success' );
                    this.app.router.redirect( '/pack/list' );
                }
                else
                {
                    this.app.view.toast( 'Failed to create pack', 'error' );
                }
            }
            catch( e )
            {
                console.error( e );
                this.app.view.unblockUI( pageContent );
                this.app.view.toast( 'An error occurred while creating the pack', 'error' );
            }
        });
    }

    _escapeHtml( str )
    {
        const div = document.createElement( 'div' );
        div.appendChild( document.createTextNode( str ) );
        return div.innerHTML;
    }

    // ─── Render ─────────────────────────────────────────────

    render()
    {
        this.app.view.render(
            this.app.view.pageContent,
            template,
            {
                availableTemplates : this.availableTemplates
            }
        );

        this._updateCounts();
    }

    getContentHeaderData()
    {
        return {
            title: 'Create a new pack',
            breadcrumb: [ 'Work', 'Packs', 'New pack' ]
        };
    }
}
